import React, { useMemo } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { PlatformPressable } from '@react-navigation/elements';
import { useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import IcomoonIcon from '../components/IcomoonIcon';
import { Icon } from '../components/Icon';
import tw from '../styles/tailwind';
import useStaff from '../hooks/useStaff';

import GuardRegistrationScreen from '../screens/Community/Guard/GuardRegistrationScreen';
import GuardAccessScreen from '../screens/Community/Guard/GuardAccessScreen';
import GuardLogScreen from '../screens/Community/Guard/GuardLogScreen';
import GuardTicketsScreen from '../screens/Community/Guard/Tickets/GuardTicketsScreen';
import GuardTicketDetailScreen from '../screens/Community/Guard/Tickets/GuardTicketDetailScreen';
import SettingsScreen from '../screens/Main/SettingsScreen';


const Stack = createNativeStackNavigator();
const BottomTab = createBottomTabNavigator();


export default function GuardStackNavigator({ navigation, route }: { navigation: any; route: any }) {
  const { guardCommunities } = useStaff();
  // Desde Administración llega con la comunidad elegida; como vista única, la primera.
  const communityId = route.params?.communityId ?? guardCommunities?.[0]?.uuid;
  // Abierta desde Administración: en lugar de Mi Perfil, un tab para regresar.
  const onExit = route.name === 'Guard.Stack' ? () => navigation.goBack() : undefined;

  return (
    <Stack.Navigator>
      <Stack.Screen
        name="Guard.Home"
        options={{ headerShown: false }}
      >
        {() => <GuardBottomTabNavigator communityId={communityId} onExit={onExit} />}
      </Stack.Screen>

      <Stack.Screen
        name="Guard.Tickets.Detail"
        component={GuardTicketDetailScreen}
        options={{ title: "", headerShown: false }}
      />
    </Stack.Navigator>
  );
}



/**
 * Icono de tab del vigilante. La sección seleccionada lleva fondo verde (primary, en
 * tabBarActiveBackgroundColor, abarca icono y nombre) y por eso sus iconos activos son
 * blancos; sin el fondo quedaban blanco sobre blanco.
 */
function GuardTabIcon({ name, focused, activeVariant = 'active' }: { name: any; focused: boolean; activeVariant?: string }) {
  return (
    <Icon name={name} variant={focused ? activeVariant : 'default'} size={22} style={tw`-mb-1.5`} />
  );
}


function GuardBottomTabNavigator({ communityId, onExit }: { communityId?: string; onExit?: () => void }) {
  const insets = useSafeAreaInsets();

  return (
    <BottomTab.Navigator
      // tabBar={props => <GuardTabBar {...props} />}
      screenOptions={{
        // Sección seleccionada: cuadro redondeado verde que abarca icono y nombre, nombre en blanco.
        tabBarActiveTintColor: tw.color('white'),
        tabBarActiveBackgroundColor: tw.color('primary'),
        tabBarItemStyle: { marginHorizontal: 4, marginVertical: 4 },
        // El fondo lo pinta el botón interno con borderRadius 0 fijo; se redondea aquí,
        // después de su estilo (recortar el contenedor no funcionó en el teléfono).
        tabBarButton: (props: any) => <PlatformPressable {...props} style={[props.style, { borderRadius: 12 }]} />,
        tabBarStyle: { height: 57 + insets.bottom },
      }}
    >
      <BottomTab.Screen
        name="Guard.Registration"
        component={GuardRegistrationScreen}
        initialParams={{ communityId }}
        options={{
          title: 'Registro',
          headerShown: false,
          tabBarIcon: ({ focused }) => <GuardTabIcon name="navGuardRegistro" focused={focused} />,
        }}
      />

      <BottomTab.Screen
        name="Guard.Access"
        component={GuardAccessScreen}
        initialParams={{ communityId }}
        options={{
          title: 'Acceso',
          headerShown: false,
          tabBarIcon: ({ focused }) => <GuardTabIcon name="navGuardAccesos" focused={focused} />,
        }}
      />

      <BottomTab.Screen
        name="Guard.Log"
        component={GuardLogScreen}
        initialParams={{ communityId }}
        options={{
          title: 'Bitácora',
          headerShown: false,
          tabBarIcon: ({ focused }) => <GuardTabIcon name="navGuardBitacora" focused={focused} />,
        }}
      />

      <BottomTab.Screen
        name="Guard.Tickets"
        component={GuardTicketsScreen}
        initialParams={{ communityId }}
        options={{
          title: 'Incidencias',
          headerShown: false,
          tabBarIcon: ({ focused }) => <GuardTabIcon name="navGuardIncidencias" focused={focused} />,
        }}
      />

      {
        onExit ?
          <BottomTab.Screen
            name="Guard.Exit"
            component={EmptyScreen}
            listeners={{
              tabPress: e => {
                e.preventDefault();
                onExit();
              },
            }}
            options={{
              title: 'Regresar',
              headerShown: false,
              tabBarIcon: ({ color }) => <Ionicons
                name="arrow-back-circle-outline"
                color={color}
                size={24}
                style={tw`-mb-1.5`}
              />,
            }}
          />
        :
          <BottomTab.Screen
            name="Guard.Settings"
            component={SettingsScreen}
            options={{
              title: 'Mi Perfil',
              headerShown: true,
              tabBarIcon: ({ focused }) => <GuardTabIcon name="navPerfil" focused={focused} activeVariant="white" />,
            }}
          />
      }
    </BottomTab.Navigator>
  );
}


// El tab Regresar nunca se muestra: su tabPress sale de la consola.
function EmptyScreen() {
  return null;
}
